import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui';
import { getAvailableGroupsStats } from '../../../services/projectService';
import groupService from '../../../services/groupService';
import GroupStats from '../components/GroupStats';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;

const AddGroupsDialog = ({
  isOpen,
  onClose,
  selectedGroups,
  onGroupSelection,
  onAddGroups,
  projectId,
  excludeChatIds = [],
}) => {
  const [availableStats, setAvailableStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  const [language, setLanguage] = useState('');
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const debounceTimerRef = useRef(null);

  const fetchAvailableStats = useCallback(async () => {
    if (!projectId) return;
    setStatsLoading(true);
    try {
      const stats = await getAvailableGroupsStats(projectId);
      setAvailableStats(stats);
    } catch (error) {
      console.error('Failed to load available groups stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchAvailableStats();
    }
  }, [fetchAvailableStats, isOpen, projectId]);

  // Reset transient state when the dialog opens/closes.
  useEffect(() => {
    if (!isOpen) {
      setSearchInput('');
      setSearchQuery('');
      setCategory('');
      setLanguage('');
      setResults([]);
      setPagination(null);
      setPage(1);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [isOpen]);

  // Server-side search: debounce the input box so we don't hammer /api/groups.
  const handleSearchInputChange = (event) => {
    const nextValue = event.target.value;
    setSearchInput(nextValue);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(nextValue.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  // Server-side search via /api/groups. We filter out chats already in the
  // project client-side so we don't depend on backend exclusion logic here.
  const fetchResults = useCallback(async () => {
    if (!isOpen) return;
    setResultsLoading(true);
    try {
      const filters = { subscription_status: 'subscribed' };
      if (searchQuery) filters.search = searchQuery;
      if (category) filters.category = category;
      if (language) filters.language = language;

      const response = await groupService.getGroups(filters, { page, pageSize: PAGE_SIZE });
      const rows = Array.isArray(response?.groups) ? response.groups : [];
      const excludeSet = new Set(excludeChatIds.map(String));
      setResults(rows.filter((group) => !excludeSet.has(String(group.chat_id))));
      setPagination(response?.pagination || null);
    } catch (error) {
      console.error('Failed to load available groups:', error);
      setResults([]);
      setPagination(null);
    } finally {
      setResultsLoading(false);
    }
  }, [category, excludeChatIds, isOpen, language, page, searchQuery]);

  useEffect(() => {
    void fetchResults();
  }, [fetchResults]);

  useEffect(() => () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  const handlePageChange = (nextPage) => {
    if (!pagination) return;
    if (nextPage < 1 || nextPage > pagination.pages) return;
    setPage(nextPage);
  };

  const handleGroupSelection = (event) => {
    const selectedIds = new Set(
      Array.from(event.target.selectedOptions, (option) => option.value)
    );
    onGroupSelection(
      results.filter((group) => selectedIds.has(String(group.chat_id)))
    );
  };

  if (!isOpen) return null;

  const totalMatches = pagination?.total ?? results.length;

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Add Groups to Project
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Search subscribed groups and select which ones to monitor for keywords.
            </p>

            {/* Available Groups Statistics */}
            <div className="mb-4">
              <GroupStats
                stats={availableStats}
                title="Available Groups"
                isLoading={statsLoading}
              />
            </div>

            <div className="mb-4 space-y-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="search"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  placeholder="Search by title or @username..."
                  aria-label="Search available groups"
                  className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor="add-groups-category" className="flex items-center gap-1 text-xs text-gray-600">
                  <span>Category</span>
                  <select
                    id="add-groups-category"
                    value={category}
                    onChange={handleFilterChange(setCategory)}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm bg-white"
                  >
                    <option value="">All</option>
                    <option value="crypto">Crypto</option>
                    <option value="trading">Trading</option>
                    <option value="news">News</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label htmlFor="add-groups-language" className="flex items-center gap-1 text-xs text-gray-600">
                  <span>Language</span>
                  <select
                    id="add-groups-language"
                    value={language}
                    onChange={handleFilterChange(setLanguage)}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm bg-white"
                  >
                    <option value="">All</option>
                    <option value="ru">Russian</option>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="pt">Portuguese</option>
                    <option value="ar">Arabic</option>
                    <option value="fa">Persian</option>
                    <option value="hi">Hindi</option>
                    <option value="zh">Chinese</option>
                  </select>
                </label>
                <div className="ml-auto text-xs text-gray-500" aria-live="polite">
                  {resultsLoading ? 'Searching…' : `${totalMatches} matching`}
                </div>
              </div>
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Groups
              </label>
              <select
                multiple
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={selectedGroups.map((group) => String(group.chat_id))}
                onChange={handleGroupSelection}
                size={8}
              >
                {results.map((group) => (
                  <option key={group.chat_id} value={group.chat_id}>
                    {group.title || 'Unnamed Group'} {group.chat_username ? `(@${group.chat_username})` : ''} {group.member_count ? `· ${group.member_count}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple groups.</p>
            </div>

            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1 || resultsLoading}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= pagination.pages || resultsLoading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button
              variant="primary"
              onClick={onAddGroups}
              className="sm:ml-3"
              disabled={selectedGroups.length === 0}
            >
              Add {selectedGroups.length > 0 ? `(${selectedGroups.length})` : 'Selected Groups'}
            </Button>
            <Button
              variant="secondary"
              onClick={onClose}
              className="mt-3 sm:mt-0"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddGroupsDialog;
